import { useCallback, useMemo, useState } from 'react';

// Utils
import * as translator from '../../../../utils/translator';

// Icons
import LocationIcon from '../../../../icons/Location.svg';
import EditIcon from '../../../../icons/secondery green/Pencil.svg';
import DeleteIcon from '../../../../icons/Red-trash.svg';
import CheckmarkIcon from '../../../../icons/Approved.svg';
import PortaledModal from '../../../../components/Modals/PortaledModal';

interface AddressCardProps {
  id: number;
  first_name?: string;
  last_name?: string;
  address: string;
  packstation_address?: string;
  packstation_number?: string;
  company?: string;
  zip: string;
  city: string;
  country_id: string;
  phone: string;
  isDefault?: boolean;
  language: string;
  isOnlyAddress?: boolean;
  cardType?: string;
  className?: string;
  handleEdit?: () => void;
  handleDelete?: () => void;
  handleSetDefault?: () => void;
  headerTitle?: string;
}

const AddressCard = ({ id, first_name = '', last_name = '', address, packstation_address, packstation_number, company, zip, city, country_id, phone, isDefault = false, isOnlyAddress = false, cardType, className = '', handleDelete, handleEdit, handleSetDefault, language, headerTitle }: AddressCardProps) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const toggleDeleteModal = useCallback(() => {
    setShowDeleteModal(!showDeleteModal);
  }, [showDeleteModal]);

  const handleDeleteConfirm = useCallback(() => {
    if (handleDelete) {
      handleDelete();
      toggleDeleteModal();
    }
  }, [handleDelete, toggleDeleteModal]);

  const defaultStatusElement = useMemo(() => {
    if (isDefault) {
      return (
        <div id='default-address-badge' className="bg-[#19D845] text-[#FFFFFF] text-xs font-medium px-2 py-1 rounded-full flex items-center gap-x-1">
          <img id='default-address-badge-icon' className='w-[12px] h-[12px]' src={CheckmarkIcon} alt="Checkmark" />
          {translator.translate(language, "my_account", `DeliveryAddressV2/default`)}
        </div>
      );
    }

    return (
      <button
        id='set-default-address-button'
        onClick={handleSetDefault}
        className="text-[#19D845] cursor-pointer hover:text-[#19D845]/80 text-xs md:text-sm border-solid border border-[#19D845] bg-transparent !rounded-[4px] py-[2px] px-[6px] transition-colors"
      >
        {translator.translate(language, "my_account", `DeliveryAddressV2/saveAsDefault`)}
      </button>
    );

  }, [isDefault, language, handleSetDefault]);

  // Format full name from first and last name
  const fullName = useMemo(() => {
    return `${first_name} ${last_name}`.trim();
  }, [first_name, last_name, language]);

  // Determine what address lines to show
  const addressDetails = useMemo(() => {
    const details = [];

    if (company) details.push(company);

    if (packstation_address && packstation_number) {
      details.push(`${packstation_address} ${packstation_number}`);
    } else if (address) {
      details.push(address);
    }

    return details;
  }, [address, company, packstation_address, packstation_number, language]);

  return (
    <>
      <div id='address-card' className={`w-full max-h-[300px] sm:max-h-full rounded-lg border border-zinc-600 bg-white shadow-sm overflow-hidden flex flex-col justify-between ${className}`} style={{ border: '1px solid #F7F7F7' }}>

        <div id={`address-card-${id}`} className="flex flex-col h-full">
          {/* Address Card Header */}
          <div id='address-card-header' className="flex justify-between items-center p-4" style={{ borderBottom: '1px solid #F7F7F7' }}>
            <div id='address-card-header-name' className="flex items-center gap-x-1 max-w-[65%] overflow-hidden">
              <div className="w-5 h-5 rounded-full border border-gray-300 flex items-center justify-center flex-shrink-0">
                <img id='location-icon-image' className='w-[16px] h-[16px]' src={LocationIcon} alt="Location" />
              </div>
              <h3 className="w-full font-semibold text-[#0D2C54] truncate">{headerTitle}</h3>
            </div>
            {defaultStatusElement}
          </div>

          {/* Address Card Info */}
          <section id='address-card-info' className="px-4 py-6 space-y-2">
            <p id='address-card-name' className="text-[#616161] font-medium">{fullName}</p>
            {addressDetails.map((detail, index) => (
              <p key={index} id={`address-card-detail-${index}`} className="text-[#616161] text-xs">{detail}</p>
            ))}
            <p id='address-card-zip-city' className="text-[#616161] text-xs">{zip || ''} {city || ''}</p>
            <p id='address-card-country' className="text-[#616161] text-xs">{country_id.toUpperCase() || ''}</p>
            <p id='address-card-phone' className="text-[#616161] text-xs">{phone || ''}</p>
          </section>
        </div>

        {/* Address Card Buttons */}
        <div id='address-card-buttons' className="flex justify-between bg-[#F7F7F7] px-8 py-4">
          <button
            id='edit-address-button'
            onClick={handleEdit}
            className="text-[#0A9281] cursor-pointer hover:text-[#0A9281]/80 text-sm font-medium flex items-center gap-1 transition-colors bg-transparent border-none"
          >
            <img id='edit-address-icon' className='h-[12px] w-[12px]' src={EditIcon} alt="Edit" />
            <span id='edit-address-button-text' className='text-[#0A9281]'>{translator.translate(language, "my_account", `DeliveryAddressV2/edit`)}</span>
          </button>

          <button
            id='delete-address-button'
            onClick={toggleDeleteModal}
            className="text-sm font-medium flex items-center gap-1 transition-colors border-solid border rounded-md px-2 py-1 text-[#EF4747] cursor-pointer hover:text-[#EF4747]/80 bg-[#FFFFFF] border-[#EF4747]"
          >
            <img id='delete-address-icon' className='h-[12px] w-[12px]' src={DeleteIcon} alt="Delete" />
            <span id='delete-address-button-text'>{translator.translate(language, "my_account", `DeliveryAddressV2/delete`)}</span>
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <PortaledModal
          shouldShowModal={showDeleteModal}
          shouldBackgroundClickHideModal={true}
          hideModal={toggleDeleteModal}
        >
          <DeleteConfirmationModal language={language} toggleDeleteModal={toggleDeleteModal} handleDeleteConfirm={handleDeleteConfirm} />
        </PortaledModal>
      )}
    </>
  );
};

export default AddressCard;

// Delete confirmation modal content
interface DeleteConfirmationModalProps {
  language: string;
  toggleDeleteModal: () => void;
  handleDeleteConfirm: () => void;
}

const DeleteConfirmationModal = ({ language, toggleDeleteModal, handleDeleteConfirm }: DeleteConfirmationModalProps) => (
  <div id="delete-address-modal" className="cancel-subscription-modal-popup-container px-6 py-8 overflow-hidden">
    <div className="flex items-center gap-x-2">
      <img src={DeleteIcon} className='w-[16px] h-[16px] my-auto' alt="Delete" />
      <h2 id="delete-address-modal-title" className="popup-title my-auto">{translator.translate(language, "my_account", `DeliveryAddressV2/deleteAddress`)}</h2>
    </div>

    <p id="delete-address-modal-message" className="popup-description">
      {translator.translate(language, "my_account", `DeliveryAddressV2/deleteAddressMessage`)}
    </p>

    <div id="delete-address-modal-buttons" className="flex items-center gap-x-4">
      <button
        id="delete-address-cancel-button"
        onClick={toggleDeleteModal}
        className="popup-button-secondary"
      >
        {translator.translate(language, "my_account", `DeliveryAddressV2/cancel`)}
      </button>

      <button
        id="delete-address-confirm-button"
        onClick={handleDeleteConfirm}
        className="popup-button-primary bg-red-500 hover:bg-red-600"
      >
        {translator.translate(language, "my_account", `DeliveryAddressV2/delete`)}
      </button>
    </div>
  </div>
);